import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../components/admin/layout/Layout';
import Button from '../components/shared/Button';
import Input from '../components/shared/Input';
import Modal from '../components/shared/Modal';
import Badge from '../components/shared/Badge';
import { adminApi } from '../api/admin';
import type {
  Project,
  ControlCategory,
  ControlButton,
  JoystickConfig,
} from '../types';
import type { ApiError } from '../api/client';

const defaultJoystickConfig: JoystickConfig = {
  movementEnabled: true,
  cameraEnabled: true,
  movementSensitivity: 1,
  cameraSensitivity: 1,
};

const PlayerControls: React.FC = () => {
  const [projects, setProjects] = useState<Project[]>([]);
  const [selectedProjectId, setSelectedProjectId] = useState<string>('');
  const [categories, setCategories] = useState<ControlCategory[]>([]);
  const [buttons, setButtons] = useState<ControlButton[]>([]);
  const [joystickConfig, setJoystickConfig] = useState<JoystickConfig>(defaultJoystickConfig);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedCategoryId, setSelectedCategoryId] = useState<string | null>(null);
  const [isCategoryModalOpen, setIsCategoryModalOpen] = useState(false);
  const [isButtonModalOpen, setIsButtonModalOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState<ControlCategory | null>(null);
  const [editingButton, setEditingButton] = useState<ControlButton | null>(null);
  const [categoryName, setCategoryName] = useState('');
  const [buttonForm, setButtonForm] = useState({
    name: '',
    x: '',
    y: '',
    z: '',
    yaw: '',
  });
  const [submitLoading, setSubmitLoading] = useState(false);
  const [joystickSaving, setJoystickSaving] = useState(false);

  const fetchProjects = useCallback(async () => {
    try {
      const data = await adminApi.projects.list();
      setProjects(data);
      if (data.length > 0 && !selectedProjectId) setSelectedProjectId(data[0].id);
    } catch (err) {
      setError((err as ApiError).message || 'Failed to load projects');
    } finally {
      setLoading(false);
    }
  }, [selectedProjectId]);

  useEffect(() => {
    document.title = 'Player Controls';
    return () => { document.title = 'VR Companion Admin'; };
  }, []);

  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  const fetchCategories = useCallback(async () => {
    if (!selectedProjectId) {
      setCategories([]);
      return;
    }
    setError('');
    try {
      const data = await adminApi.controls.categories.list(selectedProjectId);
      setCategories(data);
      if (!data.some((c) => c.id === selectedCategoryId)) setSelectedCategoryId(null);
    } catch (err) {
      setError((err as ApiError).message || 'Failed to load categories');
    }
  }, [selectedProjectId, selectedCategoryId]);

  const fetchButtons = useCallback(async () => {
    if (!selectedProjectId) {
      setButtons([]);
      return;
    }
    setError('');
    try {
      const data = await adminApi.controls.buttons.listByProject(selectedProjectId);
      setButtons(data);
    } catch (err) {
      setError((err as ApiError).message || 'Failed to load buttons');
    }
  }, [selectedProjectId]);

  const fetchJoystickConfig = useCallback(async () => {
    if (!selectedProjectId) return;
    try {
      const data = await adminApi.controls.joystickConfig.get(selectedProjectId);
      setJoystickConfig(data);
    } catch {
      setJoystickConfig(defaultJoystickConfig);
    }
  }, [selectedProjectId]);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  useEffect(() => {
    fetchButtons();
  }, [fetchButtons]);

  useEffect(() => {
    fetchJoystickConfig();
  }, [fetchJoystickConfig]);

  const selectedCategoryButtons = selectedCategoryId
    ? buttons.filter((b) => b.categoryId === selectedCategoryId)
    : [];

  const handleAddCategory = () => {
    setEditingCategory(null);
    setCategoryName('');
    setIsCategoryModalOpen(true);
  };

  const handleEditCategory = (cat: ControlCategory) => {
    setEditingCategory(cat);
    setCategoryName(cat.name);
    setIsCategoryModalOpen(true);
  };

  const handleSaveCategory = async () => {
    if (!categoryName.trim() || !selectedProjectId) return;
    setSubmitLoading(true);
    setError('');
    try {
      if (editingCategory) {
        await adminApi.controls.categories.update(editingCategory.id, {
          name: categoryName.trim(),
        });
      } else {
        await adminApi.controls.categories.create({
          name: categoryName.trim(),
          projectId: selectedProjectId,
        });
      }
      setIsCategoryModalOpen(false);
      setCategoryName('');
      setEditingCategory(null);
      fetchCategories();
    } catch (err) {
      setError((err as ApiError).message || 'Failed to save category');
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleDeleteCategory = async (cat: ControlCategory) => {
    if (!window.confirm(`Delete category "${cat.name}"? Buttons in this category will need to be deleted separately.`)) return;
    setError('');
    try {
      await adminApi.controls.categories.delete(cat.id);
      const toDelete = buttons.filter((b) => b.categoryId === cat.id);
      for (const b of toDelete) await adminApi.controls.buttons.delete(b.id);
      if (selectedCategoryId === cat.id) setSelectedCategoryId(null);
      fetchCategories();
      fetchButtons();
    } catch (err) {
      setError((err as ApiError).message || 'Failed to delete');
    }
  };

  const handleAddButton = () => {
    if (!selectedCategoryId) {
      alert('Please select a category first');
      return;
    }
    setEditingButton(null);
    setButtonForm({ name: '', x: '', y: '', z: '', yaw: '' });
    setIsButtonModalOpen(true);
  };

  const handleEditButton = (btn: ControlButton) => {
    setEditingButton(btn);
    setButtonForm({
      name: btn.name,
      x: String(btn.position.x),
      y: String(btn.position.y),
      z: String(btn.position.z),
      yaw: btn.rotationYaw != null ? String(btn.rotationYaw) : '',
    });
    setIsButtonModalOpen(true);
  };

  const handleSaveButton = async () => {
    if (!buttonForm.name.trim() || !selectedCategoryId) return;
    const x = parseFloat(buttonForm.x);
    const y = parseFloat(buttonForm.y);
    const z = parseFloat(buttonForm.z);
    if (Number.isNaN(x) || Number.isNaN(y) || Number.isNaN(z)) {
      alert('Please enter valid X, Y, and Z coordinates');
      return;
    }
    setSubmitLoading(true);
    setError('');
    try {
      const position = { x, y, z };
      const rotationYaw = buttonForm.yaw ? parseFloat(buttonForm.yaw) : undefined;
      if (editingButton) {
        await adminApi.controls.buttons.update(editingButton.id, {
          name: buttonForm.name.trim(),
          actionType: 'TELEPORT',
          position,
          rotationYaw,
        });
      } else {
        await adminApi.controls.buttons.create({
          categoryId: selectedCategoryId,
          name: buttonForm.name.trim(),
          actionType: 'TELEPORT',
          position,
          rotationYaw,
        });
      }
      setIsButtonModalOpen(false);
      setButtonForm({ name: '', x: '', y: '', z: '', yaw: '' });
      setEditingButton(null);
      fetchButtons();
    } catch (err) {
      setError((err as ApiError).message || 'Failed to save button');
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleDeleteButton = async (btn: ControlButton) => {
    if (!window.confirm(`Delete button "${btn.name}"?`)) return;
    setError('');
    try {
      await adminApi.controls.buttons.delete(btn.id);
      fetchButtons();
    } catch (err) {
      setError((err as ApiError).message || 'Failed to delete');
    }
  };

  const handleJoystickChange = (patch: Partial<JoystickConfig>) => {
    const next = { ...joystickConfig, ...patch };
    setJoystickConfig(next);
    if (!selectedProjectId) return;
    setJoystickSaving(true);
    adminApi.controls.joystickConfig
      .update(selectedProjectId, next)
      .then(() => setJoystickConfig(next))
      .catch(() => setError('Failed to save joystick config'))
      .finally(() => setJoystickSaving(false));
  };

  const movementSensitivityPercent = Math.round(joystickConfig.movementSensitivity * 100);
  const cameraSensitivityPercent = Math.round(joystickConfig.cameraSensitivity * 100);

  return (
    <Layout pageTitle="Player Controls Configuration">
      <div className="space-y-4">
        {error && (
          <div className="error-alert">
            <p className="text-sm text-red-800">{error}</p>
          </div>
        )}
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-4">
          <label className="text-sm font-medium text-gray-700">Project</label>
          <select
            value={selectedProjectId}
            onChange={(e) => setSelectedProjectId(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 w-full sm:w-auto max-w-xs"
          >
            <option value="">Select project</option>
            {projects.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      {loading ? (
        <p className="text-gray-500 mt-4">Loading...</p>
      ) : !selectedProjectId ? (
        <p className="text-gray-500 mt-4">Select a project to configure controls.</p>
      ) : (
        <div className="player-controls-container">
          {/* Categories panel – stacks above on small/medium, left column on desktop */}
          <div className="player-controls-card">
            <div className="category-sidebar">
              <div className="category-header">
                <h3 className="section-title">Categories</h3>
                <Button size="sm" onClick={handleAddCategory}>
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                </Button>
              </div>
              <div className="divide-y divide-gray-200">
                {categories.map((cat) => (
                  <div
                    key={cat.id}
                    className={`category-item ${selectedCategoryId === cat.id ? 'category-item-active' : ''}`}
                    onClick={() => setSelectedCategoryId(cat.id)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1 min-w-0">
                        <p className="category-name">{cat.name}</p>
                        <p className="category-count">
                          {buttons.filter((b) => b.categoryId === cat.id).length} buttons
                        </p>
                      </div>
                      <div className="category-actions">
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEditCategory(cat);
                          }}
                          className="category-action-btn"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                        </button>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteCategory(cat);
                          }}
                          className="category-action-btn category-action-btn-delete"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Teleport Buttons + Joystick – stacked in one column, no overlap */}
          <div className="flex flex-col gap-6 min-w-0">
            <div className="section-card player-controls-card">
              <div className="section-header">
                <div className="player-controls-header-row">
                  <div className="min-w-0">
                    <h3 className="section-title">Teleport Buttons</h3>
                    {selectedCategoryId && (
                      <p className="text-sm text-gray-500 mt-1">
                        Category: {categories.find((c) => c.id === selectedCategoryId)?.name}
                      </p>
                    )}
                  </div>
                  <Button
                    onClick={handleAddButton}
                    disabled={!selectedCategoryId}
                    title={!selectedCategoryId ? 'Select a category first' : ''}
                  >
                    Add Button
                  </Button>
                </div>
              </div>

              {!selectedCategoryId ? (
                <div className="empty-state">
                  <h3 className="empty-state-title">No category selected</h3>
                  <p className="empty-state-description">Select a category from the left to manage buttons</p>
                </div>
              ) : selectedCategoryButtons.length === 0 ? (
                <div className="empty-state">
                  <h3 className="empty-state-title">No buttons</h3>
                  <p className="empty-state-description">Get started by creating a new teleport button</p>
                  <div className="mt-6">
                    <Button onClick={handleAddButton}>Add Button</Button>
                  </div>
                </div>
              ) : (
                <div className="divide-y divide-gray-200">
                  {selectedCategoryButtons.map((btn) => (
                    <div key={btn.id} className="button-list-item">
                      <div className="button-list-header">
                        <div className="flex-1">
                          <div className="flex items-center space-x-3">
                            <h4 className="button-list-name">{btn.name}</h4>
                            <Badge variant="info">{btn.actionType}</Badge>
                          </div>
                          <div className="button-list-coords">
                            <div>
                              <span className="coord-label">X:</span>
                              <span className="coord-value">{btn.position.x}</span>
                            </div>
                            <div>
                              <span className="coord-label">Y:</span>
                              <span className="coord-value">{btn.position.y}</span>
                            </div>
                            <div>
                              <span className="coord-label">Z:</span>
                              <span className="coord-value">{btn.position.z}</span>
                            </div>
                            <div>
                              <span className="coord-label">Yaw:</span>
                              <span className="coord-value">{btn.rotationYaw ?? 'N/A'}</span>
                            </div>
                          </div>
                        </div>
                        <div className="flex space-x-2">
                          <Button variant="outline" size="sm" onClick={() => handleEditButton(btn)}>
                            Edit
                          </Button>
                          <Button variant="danger" size="sm" onClick={() => handleDeleteButton(btn)}>
                            Delete
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="joystick-section player-controls-card">
              <div className="joystick-header">
                <h3 className="joystick-title">Joystick Configuration</h3>
                <p className="joystick-description">Configure movement and camera controls {joystickSaving && '(saving...)'}</p>
              </div>
              <div className="joystick-content">
                <div className="joystick-config">
                  <div className="joystick-config-header">
                    <div>
                      <h4 className="joystick-config-title">Movement Joystick</h4>
                      <p className="joystick-config-desc">Controls: Forward, Backward, Left, Right</p>
                    </div>
                    <label className="toggle-switch">
                      <input
                        type="checkbox"
                        checked={joystickConfig.movementEnabled}
                        onChange={(e) => handleJoystickChange({ movementEnabled: e.target.checked })}
                      />
                      <div className="toggle-slider"></div>
                      <span className="toggle-label">
                        {joystickConfig.movementEnabled ? 'Enabled' : 'Disabled'}
                      </span>
                    </label>
                  </div>
                  {joystickConfig.movementEnabled && (
                    <div className="sensitivity-control">
                      <label className="sensitivity-label">
                        Sensitivity: {movementSensitivityPercent}%
                      </label>
                      <input
                        type="range"
                        min="0"
                        max="300"
                        value={movementSensitivityPercent}
                        onChange={(e) =>
                          handleJoystickChange({
                            movementSensitivity: parseInt(e.target.value, 10) / 100,
                          })
                        }
                        className="range-slider"
                      />
                    </div>
                  )}
                </div>
                <div className="joystick-config">
                  <div className="joystick-config-header">
                    <div>
                      <h4 className="joystick-config-title">Camera Joystick</h4>
                      <p className="joystick-config-desc">Controls: Look Left/Right, Look Up/Down</p>
                    </div>
                    <label className="toggle-switch">
                      <input
                        type="checkbox"
                        checked={joystickConfig.cameraEnabled}
                        onChange={(e) => handleJoystickChange({ cameraEnabled: e.target.checked })}
                      />
                      <div className="toggle-slider"></div>
                      <span className="toggle-label">
                        {joystickConfig.cameraEnabled ? 'Enabled' : 'Disabled'}
                      </span>
                    </label>
                  </div>
                  {joystickConfig.cameraEnabled && (
                    <div className="sensitivity-control">
                      <label className="sensitivity-label">
                        Sensitivity: {cameraSensitivityPercent}%
                      </label>
                      <input
                        type="range"
                        min="0"
                        max="300"
                        value={cameraSensitivityPercent}
                        onChange={(e) =>
                          handleJoystickChange({
                            cameraSensitivity: parseInt(e.target.value, 10) / 100,
                          })
                        }
                        className="range-slider"
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <Modal
        isOpen={isCategoryModalOpen}
        onClose={() => {
          setIsCategoryModalOpen(false);
          setCategoryName('');
          setEditingCategory(null);
        }}
        title={editingCategory ? 'Edit Category' : 'Add Category'}
        footer={
          <>
            <Button variant="outline" onClick={() => setIsCategoryModalOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveCategory} disabled={submitLoading || !categoryName.trim()}>
              {submitLoading ? 'Saving...' : 'Save'}
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <Input
            label="Category Name"
            placeholder="e.g., Rooms, Furniture, Actions"
            value={categoryName}
            onChange={(e) => setCategoryName(e.target.value)}
          />
        </div>
      </Modal>

      <Modal
        isOpen={isButtonModalOpen}
        onClose={() => {
          setIsButtonModalOpen(false);
          setButtonForm({ name: '', x: '', y: '', z: '', yaw: '' });
          setEditingButton(null);
        }}
        title={editingButton ? 'Edit Teleport Button' : 'Add Teleport Button'}
        footer={
          <>
            <Button variant="outline" onClick={() => setIsButtonModalOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveButton} disabled={submitLoading || !buttonForm.name.trim()}>
              {submitLoading ? 'Saving...' : 'Save'}
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <Input
            label="Button Name"
            placeholder="e.g., Bedroom, Chair, Bed"
            value={buttonForm.name}
            onChange={(e) => setButtonForm({ ...buttonForm, name: e.target.value })}
          />
          <div className="grid grid-cols-3 gap-4">
            <Input
              label="X Coordinate"
              type="number"
              step="0.1"
              placeholder="0.0"
              value={buttonForm.x}
              onChange={(e) => setButtonForm({ ...buttonForm, x: e.target.value })}
            />
            <Input
              label="Y Coordinate"
              type="number"
              step="0.1"
              placeholder="0.0"
              value={buttonForm.y}
              onChange={(e) => setButtonForm({ ...buttonForm, y: e.target.value })}
            />
            <Input
              label="Z Coordinate"
              type="number"
              step="0.1"
              placeholder="0.0"
              value={buttonForm.z}
              onChange={(e) => setButtonForm({ ...buttonForm, z: e.target.value })}
            />
          </div>
          <Input
            label="Rotation (Yaw) - Optional"
            type="number"
            step="1"
            placeholder="0"
            value={buttonForm.yaw}
            onChange={(e) => setButtonForm({ ...buttonForm, yaw: e.target.value })}
          />
        </div>
      </Modal>
    </Layout>
  );
};

export default PlayerControls;
